import React, { useCallback, useEffect, useState } from 'react';
import { LinkPromptDialog } from '../common/LinkPromptDialog';
import {
    Box, Paper, Typography, Button, IconButton, Divider, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, Tooltip,
    CircularProgress, Alert, Snackbar, Menu, MenuItem, ListItemIcon,
    ListItemText, Drawer, List, ListItem, ListItemButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import HistoryIcon from '@mui/icons-material/History';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import TableChartIcon from '@mui/icons-material/TableChart';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import RestoreIcon from '@mui/icons-material/Restore';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { useNotify } from 'react-admin';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Placeholder } from '@tiptap/extension-placeholder';
import { DocumentTemplate, TemplateVersion } from './types';
import { fetchVersionContent, parseVersionsResponse } from './templatesApi';

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

// Variable categories with colors
const VARIABLE_CATEGORIES: Record<string, { label: string; color: string }> = {
    company: { label: 'Unternehmen', color: '#1976d2' },
    customer: { label: 'Kunde/Benutzer', color: '#2e7d32' },
    order: { label: 'Bestellung', color: '#ed6c02' },
    course: { label: 'Kurs', color: '#9c27b0' },
    document: { label: 'Dokument', color: '#0288d1' },
};

const categorizeVariable = (key: string): string => {
    if (key.startsWith('company') || key.startsWith('issuer') || key === 'company_name' || key === 'company_logo') return 'company';
    if (key.startsWith('customer') || key.startsWith('recipient') || key.startsWith('user') || key === 'student' || key === 'first_name' || key === 'firstName') return 'customer';
    if (key.startsWith('order') || key.startsWith('invoice') || key.startsWith('line') || key === 'subtotal' || key === 'total' || key === 'tax' || key.startsWith('subtotal') || key.startsWith('total_') || key.startsWith('tax_') || key.startsWith('payment') || key === 'paid_at' || key === 'service_date' || key === 'document_type_label' || key.endsWith('_notice')) return 'order';
    if (key.startsWith('course') || key === 'score' || key === 'completion_date') return 'course';
    return 'document';
};

// Invoice layout blocks
const INVOICE_BLOCKS = [
    {
        label: 'Rechnungskopf',
        html: `<div style="display:flex;justify-content:space-between;margin-bottom:30px;"><div>{{issuer_logo}}<br/><strong style="font-size:24px;color:{{issuer_primary_color}};">{{document_type_label}}</strong><br/>Nr. {{invoice_number}}<br/>Datum: {{invoice_date}}<br/>Leistungsdatum: {{service_date}}</div><div style="text-align:right;">{{issuer_name}}<br/>{{issuer_address}}<br/>{{issuer_email}}<br/>Steuernr.: {{issuer_tax_number}}<br/>USt-IdNr.: {{issuer_vat_id}}</div></div>`,
    },
    {
        label: 'Empfängerblock',
        html: `<div style="margin-bottom:20px;padding:15px;background:#f5f5f5;border-radius:4px;"><strong>Rechnungsempfänger:</strong><br/>{{recipient_name}}<br/>{{recipient_address}}<br/>{{recipient_vat_id}}</div>`,
    },
    {
        label: 'Positionstabelle',
        html: `<table style="width:100%;border-collapse:collapse;margin:20px 0;"><thead><tr style="background:{{issuer_primary_color}};color:#ffffff;"><th style="padding:10px;text-align:left;">Pos.</th><th style="padding:10px;text-align:left;">Leistung</th><th style="padding:10px;text-align:right;">Menge</th><th style="padding:10px;text-align:right;">Einzelpreis</th><th style="padding:10px;text-align:right;">USt.</th><th style="padding:10px;text-align:right;">Betrag</th></tr></thead><tbody>{{#each line_items}}<tr><td style="padding:8px;border-bottom:1px solid #eee;">{{@index}}</td><td style="padding:8px;border-bottom:1px solid #eee;">{{item.title}}<br/>{{item.license_type_label}} {{item.period}}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee;">{{item.quantity}}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee;">{{item.unit_price_net}}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee;">{{item.tax_rate}}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee;">{{item.total_net}}</td></tr>{{/each}}</tbody></table>`,
    },
    {
        label: 'Summenblock',
        html: `<div style="margin-left:auto;width:280px;"><table style="width:100%;"><tr><td style="padding:5px;">Summe netto:</td><td style="text-align:right;padding:5px;">{{subtotal_net}}</td></tr><tr><td style="padding:5px;">Umsatzsteuer:</td><td style="text-align:right;padding:5px;">{{tax_total}}</td></tr><tr style="font-weight:bold;border-top:2px solid #333;"><td style="padding:8px 5px;">Gesamt:</td><td style="text-align:right;padding:8px 5px;">{{total_gross}}</td></tr></table></div>`,
    },
    {
        label: 'Steuerhinweise',
        html: `<div style="margin-top:16px;font-size:12px;">{{#if small_business_notice}}<p>{{small_business_notice}}</p>{{/if}}{{#if reverse_charge_notice}}<p>{{reverse_charge_notice}}</p>{{/if}}</div>`,
    },
    {
        label: 'Fußzeile',
        html: `<div style="margin-top:40px;padding-top:20px;border-top:1px solid #ddd;font-size:12px;color:#666;text-align:center;">{{issuer_footer}}<br/>Zahlungsart: {{payment_method}} {{payment_transaction_id}}<br/>{{platform_notice}}</div>`,
    },
];

interface TemplateEditorProps {
    template: DocumentTemplate;
    onBack: () => void;
    onSave: (template: DocumentTemplate) => void;
}

// Default variables per template type — synchronisiert mit
// backend DocumentTemplate::getDefaultVariables().
export const DEFAULT_VARS: Record<string, Record<string, string>> = {
    invoice: {
        issuer_logo: 'Logo des Rechnungsstellers (Bild)', issuer_name: 'Name des Rechnungsstellers',
        issuer_address: 'Anschrift des Rechnungsstellers', issuer_tax_number: 'Steuernummer des Rechnungsstellers',
        issuer_vat_id: 'USt-IdNr. des Rechnungsstellers', issuer_email: 'E-Mail des Rechnungsstellers',
        issuer_iban: 'IBAN des Rechnungsstellers', issuer_bic: 'BIC des Rechnungsstellers',
        issuer_primary_color: 'Primärfarbe des Rechnungsstellers (Hex)', issuer_footer: 'Fußzeile des Rechnungsstellers',
        recipient_name: 'Name des Rechnungsempfängers', recipient_address: 'Anschrift des Rechnungsempfängers',
        recipient_vat_id: 'USt-IdNr. des Rechnungsempfängers',
        document_type_label: 'Belegart (Rechnung, Stornorechnung, Gutschrift)',
        invoice_number: 'Rechnungsnummer', invoice_date: 'Rechnungsdatum', service_date: 'Leistungsdatum',
        due_date: 'Fälligkeitsdatum (leer bei bezahlten Rechnungen)',
        reference_invoice_number: 'Bezugsrechnung (nur Storno/Gutschrift)',
        line_items: 'Rechnungspositionen (Schleife)',
        subtotal_net: 'Summe netto', tax_breakdown: 'Steuer je Steuersatz', tax_total: 'Umsatzsteuer gesamt',
        total_gross: 'Gesamtbetrag brutto', currency: 'Währung',
        payment_method: 'Zahlungsart', payment_transaction_id: 'Zahlungsreferenz (z. B. PayPal-Transaktion)',
        paid_at: 'Zahlungsdatum (leer, wenn nicht bezahlt)',
        reverse_charge_notice: 'Hinweis Steuerschuldnerschaft des Leistungsempfängers (nur bei Reverse Charge)',
        small_business_notice: 'Hinweis § 19 UStG (nur bei Kleinunternehmern)',
        platform_notice: 'Hinweis „erstellt über online:academy“',
    },
    email: {
        customer_name: 'Kundenname', customer_email: 'Kunden-E-Mail', customer_address: 'Kundenadresse',
        first_name: 'Vorname', user_name: 'Benutzername',
        course_name: 'Kursname (leer, wenn die Mail keinen Kurs betrifft)', course_url: 'Link zum Kurs',
        assigned_by_name: 'Zuweisende Person (bei der Willkommens-E-Mail die einladende Person; sonst leer ohne Pflichtzuweisung)',
        due_date: 'Fälligkeitsdatum (leer ohne Pflichtzuweisung)',
        order_number: 'Bestellnummer', order_date: 'Bestelldatum',
        order_status: 'Bestellstatus (bezahlt, abgeschlossen, storniert, erstattet; leer außerhalb der Bestellstatus-Mail)',
        order_items: 'Bestellte Artikel (Schleife)', order_total: 'Bestellsumme (Betrag)',
        currency: 'Währung',
        company_name: 'Firmenname', company_logo: 'Firmenlogo URL',
        company_email: 'Firmen-E-Mail', company_address: 'Firmenadresse',
        login_url: 'Login-Link', privacy_url: 'Datenschutz-Link',
        academy_url: 'Link zur online:academy', academy_logo: 'Logo online:academy URL',
    },
    certificate: {
        user_name: 'Benutzername', user_email: 'E-Mail des Benutzers',
        course_name: 'Kursname', course_description: 'Kursbeschreibung',
        completion_date: 'Abschlussdatum',
        completion_type: 'Abschlussart („bestanden" bei Kursen mit Mindestpunktzahl, sonst „teilgenommen")',
        issued_date: 'Ausstellungsdatum', issue_date: 'Ausstellungsdatum (gleichbedeutend mit issued_date)',
        score: 'Erreichte Punktzahl (z.B. 85%)', score_percent: 'Erreichte Punktzahl (gleichbedeutend mit score)',
        verification_code: 'Verifizierungscode', verification_url: 'Verifizierungs-Link',
        qr_code: 'QR-Code URL',
        company_name: 'Unternehmensname', company_logo: 'Unternehmenslogo URL',
        assigned_by_name: 'Zuweisende Person (leer ohne Pflichtzuweisung)',
    },
    'assignment-reminder': {
        firstName: 'Vorname', studentName: 'Benutzername', courseName: 'Kursname',
        dueDate: 'Fälligkeitsdatum', daysRemaining: 'Verbleibende Tage bis Fälligkeit',
        courseUrl: 'Link zum Kurs', assignedByName: 'Zuweisende Person',
        companyName: 'Firmenname', notes: 'Optionale Notiz',
        academyLogo: 'Logo online:academy URL',
    },
};

export const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onBack, onSave }) => {
    const notify = useNotify();
    const getToken = () => localStorage.getItem('token');

    const [saving, setSaving] = useState(false);
    const [saveComment, setSaveComment] = useState('');
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [subject, setSubject] = useState(template.subject ?? '');
    const hasSubject = template.type === 'email' || template.type === 'assignment-reminder';
    const [showHtml, setShowHtml] = useState(false);
    const [htmlSource, setHtmlSource] = useState('');
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);

    // Variable menu
    const [varAnchor, setVarAnchor] = useState<null | HTMLElement>(null);

    // Block menu (invoice)
    const [blockAnchor, setBlockAnchor] = useState<null | HTMLElement>(null);

    // Version history
    const [versions, setVersions] = useState<TemplateVersion[]>([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [versionsLoading, setVersionsLoading] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(template.currentVersionNumber);

    // Preview
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Revoke blob URL when component unmounts to avoid memory leak
    useEffect(() => {
        return () => {
            if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
        };
    }, [previewPdfUrl]);

    // Issue #166: Bisher gewann template.variables — bei Type-Wechsel (z. B. Rechnung → E-Mail)
    // blieben die alten Rechnungs-Variablen im Dropdown stehen, weil das Feld
    // beim ursprünglichen Anlegen persistiert wurde. Wir bevorzugen jetzt die
    // typspezifischen Defaults und nutzen template.variables nur als Fallback,
    // falls fuer den Type keine Defaults definiert sind (z. B. unbekannter Custom-Type).
    const variablesMap = DEFAULT_VARS[template.type] || template.variables || {};
    const groupedVars = Object.entries(variablesMap).reduce<Record<string, { key: string; desc: string }[]>>((acc, [key, val]) => {
        const cat = categorizeVariable(key);
        if (!acc[cat]) acc[cat] = [];
        const desc = typeof val === 'string' ? val : (val as any).description || key;
        acc[cat].push({ key, desc });
        return acc;
    }, {});

    // TipTap Editor — disable built-in extensions that we configure separately
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                // @ts-ignore — disable StarterKit's built-in link to use our configured one
                link: false,
            }),
            Underline,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Image.configure({ inline: true }),
            Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank' } }),
            Table,
            TableRow,
            TableCell,
            TableHeader,
            TextStyle,
            Color,
            Placeholder.configure({ placeholder: 'Beginnen Sie hier mit der Bearbeitung...' }),
        ],
        content: template.contentHtml || '<p></p>',
        onUpdate: () => setHasChanges(true),
    });

    // Insert variable at cursor
    const insertVariable = (varKey: string) => {
        if (!editor) return;
        editor.chain().focus().insertContent(`<span style="background:#e3f2fd;color:#1565c0;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:13px;">{{${varKey}}}</span>&nbsp;`).run();
        setVarAnchor(null);
    };

    // Insert layout block
    const insertBlock = (html: string) => {
        if (!editor) return;
        editor.chain().focus().insertContent(html).run();
        setBlockAnchor(null);
    };

    // Save
    const handleSave = async () => {
        const token = getToken();
        if (!token || !editor) return;

        setSaving(true);
        try {
            // Get HTML and clean up variable spans back to {{}} syntax for backend
            let html = editor.getHTML();
            // Convert styled variable spans back to plain {{var}}
            html = html.replace(/<span[^>]*>\{\{([^}]+)\}\}<\/span>/g, '{{$1}}');

            const response = await fetch(`${API_URL}/document-templates/${template.id}/update`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    contentHtml: html,
                    contentJson: null,
                    ...(hasSubject ? { subject } : {}),
                    _comment: saveComment || undefined,
                }),
            });

            if (!response.ok) throw new Error('Fehler beim Speichern');

            const result = await response.json();
            notify('Vorlage erfolgreich gespeichert', { type: 'success' });
            setHasChanges(false);
            setSaveComment('');
            setSaveDialogOpen(false);
            setCurrentVersion(result.template?.currentVersionNumber ?? currentVersion);
            onSave(result.template);
        } catch {
            notify('Fehler beim Speichern', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Load versions
    const loadVersions = async () => {
        const token = getToken();
        if (!token) return;
        setVersionsLoading(true);
        try {
            const res = await fetch(`${API_URL}/document-templates/${template.id}/versions`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setVersions(parseVersionsResponse(data));
            }
        } catch {} finally { setVersionsLoading(false); }
    };

    const handleRestore = async (version: TemplateVersion) => {
        const token = getToken();
        if (!editor || !token) return;
        const html = await fetchVersionContent(API_URL, template.id, version.versionNumber, token).catch(() => null);
        if (html === null) {
            notify('Version konnte nicht geladen werden', { type: 'error' });
            return;
        }
        editor.commands.setContent(html);
        setHasChanges(true);
        setHistoryOpen(false);
        notify('Version wiederhergestellt — bitte speichern um zu übernehmen', { type: 'info' });
    };

    // Preview
    const isCertificate = template.type === 'certificate';

    const handlePreviewClose = () => {
        setPreviewOpen(false);
        if (previewPdfUrl) {
            URL.revokeObjectURL(previewPdfUrl);
            setPreviewPdfUrl(null);
        }
    };

    const handlePreview = async () => {
        const token = getToken();
        if (!token || !editor) return;
        setPreviewLoading(true);
        setPreviewOpen(true);
        try {
            let html = editor.getHTML();
            html = html.replace(/<span[^>]*>\{\{([^}]+)\}\}<\/span>/g, '{{$1}}');

            if (isCertificate) {
                const res = await fetch(`${API_URL}/document-templates/${template.id}/preview-pdf`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ contentHtml: html }),
                });
                if (res.ok) {
                    const blob = await res.blob();
                    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
                    setPreviewPdfUrl(URL.createObjectURL(blob));
                }
            } else {
                const res = await fetch(`${API_URL}/document-templates/${template.id}/preview`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ contentHtml: html }),
                });
                if (res.ok) {
                    const data = await res.json();
                    setPreviewHtml(data.html || data.preview || html);
                }
            }
        } catch {} finally { setPreviewLoading(false); }
    };

    // Toggle HTML source view
    const toggleHtmlView = () => {
        if (!editor) return;
        if (showHtml) {
            // Apply HTML source back to editor
            editor.commands.setContent(htmlSource);
            setShowHtml(false);
            setHasChanges(true);
        } else {
            let html = editor.getHTML();
            html = html.replace(/<span[^>]*>\{\{([^}]+)\}\}<\/span>/g, '{{$1}}');
            setHtmlSource(html);
            setShowHtml(true);
        }
    };

    if (!editor) return <CircularProgress />;

    const isInvoice = template.type === 'invoice';

    return (
        <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
                    <Box>
                        <Typography variant="h6">{template.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={template.type} size="small" />
                            {hasChanges && <Chip label="Ungespeichert" size="small" color="warning" />}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Versionshistorie">
                        <IconButton onClick={() => { setHistoryOpen(true); loadVersions(); }}>
                            <HistoryIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="HTML-Quellcode">
                        <IconButton onClick={toggleHtmlView} color={showHtml ? 'primary' : 'default'}>
                            <CodeIcon />
                        </IconButton>
                    </Tooltip>
                    <Button variant="outlined" startIcon={<PreviewIcon />} onClick={handlePreview}>
                        Vorschau
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={() => setSaveDialogOpen(true)}
                        disabled={saving || !hasChanges}
                    >
                        Speichern
                    </Button>
                </Box>
            </Paper>

            {hasSubject && (
                <Paper sx={{ px: 2, py: 1.5, borderRadius: 0, borderTop: '1px solid #eee' }}>
                    <TextField
                        label="Betreff"
                        fullWidth
                        size="small"
                        value={subject}
                        onChange={(e) => { setSubject(e.target.value); setHasChanges(true); }}
                    />
                </Paper>
            )}

            {/* Toolbar */}
            <Paper sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', borderRadius: 0, borderTop: '1px solid #eee' }}>
                {/* Text formatting */}
                <Tooltip title="Fett"><IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} color={editor.isActive('bold') ? 'primary' : 'default'}><FormatBoldIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Kursiv"><IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} color={editor.isActive('italic') ? 'primary' : 'default'}><FormatItalicIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Unterstrichen"><IconButton size="small" onClick={() => editor.chain().focus().toggleUnderline().run()} color={editor.isActive('underline') ? 'primary' : 'default'}><FormatUnderlinedIcon fontSize="small" /></IconButton></Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Headings */}
                {[1, 2, 3].map(level => (
                    <Tooltip key={level} title={`Überschrift ${level}`}>
                        <IconButton size="small" onClick={() => editor.chain().focus().toggleHeading({ level: level as 1|2|3 }).run()} color={editor.isActive('heading', { level }) ? 'primary' : 'default'}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 14 - level }}>H{level}</Typography>
                        </IconButton>
                    </Tooltip>
                ))}

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Lists */}
                <Tooltip title="Aufzählung"><IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} color={editor.isActive('bulletList') ? 'primary' : 'default'}><FormatListBulletedIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Nummerierung"><IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} color={editor.isActive('orderedList') ? 'primary' : 'default'}><FormatListNumberedIcon fontSize="small" /></IconButton></Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Alignment */}
                <Tooltip title="Linksbündig"><IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('left').run()} color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}><FormatAlignLeftIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Zentriert"><IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('center').run()} color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}><FormatAlignCenterIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Rechtsbündig"><IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('right').run()} color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}><FormatAlignRightIcon fontSize="small" /></IconButton></Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Insert */}
                <Tooltip title="Bild einfügen">
                    <IconButton size="small" onClick={() => setImageDialogOpen(true)}>
                        <ImageIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Link einfügen">
                    <IconButton size="small" onClick={() => setLinkDialogOpen(true)}>
                        <LinkIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <LinkPromptDialog
                    open={imageDialogOpen}
                    title="Bild einfügen"
                    label="Bild-URL"
                    allowRemove={false}
                    onClose={() => setImageDialogOpen(false)}
                    onSubmit={(url) => {
                        if (url) editor.chain().focus().setImage({ src: url }).run();
                    }}
                />
                <LinkPromptDialog
                    open={linkDialogOpen}
                    title="Link einfügen"
                    label="Link-URL"
                    initialUrl={editor.getAttributes('link').href || ''}
                    onClose={() => setLinkDialogOpen(false)}
                    onSubmit={(url) => {
                        if (url === null) return;
                        if (url === '') {
                            editor.chain().focus().unsetLink().run();
                            return;
                        }
                        editor.chain().focus().setLink({ href: url }).run();
                    }}
                />
                <Tooltip title="Trennlinie">
                    <IconButton size="small" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                        <HorizontalRuleIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Tabelle einfügen">
                    <IconButton size="small" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
                        <TableChartIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Variable dropdown */}
                <Button
                    size="small"
                    startIcon={<DataObjectIcon />}
                    onClick={(e) => setVarAnchor(e.currentTarget)}
                    sx={{ textTransform: 'none' }}
                >
                    Variable
                </Button>
                <Menu anchorEl={varAnchor} open={Boolean(varAnchor)} onClose={() => setVarAnchor(null)} PaperProps={{ sx: { maxHeight: 400, width: 320 } }}>
                    {Object.entries(groupedVars).map(([cat, vars]) => [
                        <MenuItem key={`cat-${cat}`} disabled sx={{ fontSize: 12, fontWeight: 700, color: VARIABLE_CATEGORIES[cat]?.color || '#666', minHeight: 32 }}>
                            {VARIABLE_CATEGORIES[cat]?.label || cat}
                        </MenuItem>,
                        ...vars.map(v => (
                            <MenuItem key={v.key} onClick={() => insertVariable(v.key)} sx={{ fontSize: 13 }}>
                                <ListItemText primary={`{{${v.key}}}`} secondary={v.desc} primaryTypographyProps={{ fontFamily: 'monospace', fontSize: 13 }} secondaryTypographyProps={{ fontSize: 11 }} />
                            </MenuItem>
                        ))
                    ]).flat()}
                    {Object.keys(groupedVars).length === 0 && (
                        <MenuItem disabled><ListItemText primary="Keine Variablen verfügbar" /></MenuItem>
                    )}
                </Menu>

                {/* Invoice layout blocks */}
                {isInvoice && (
                    <>
                        <Button
                            size="small"
                            startIcon={<WidgetsIcon />}
                            onClick={(e) => setBlockAnchor(e.currentTarget)}
                            sx={{ textTransform: 'none' }}
                        >
                            Bausteine
                        </Button>
                        <Menu anchorEl={blockAnchor} open={Boolean(blockAnchor)} onClose={() => setBlockAnchor(null)}>
                            {INVOICE_BLOCKS.map(block => (
                                <MenuItem key={block.label} onClick={() => insertBlock(block.html)}>
                                    <ListItemIcon><WidgetsIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>{block.label}</ListItemText>
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}
            </Paper>

            {/* Editor / HTML Source */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#f5f5f5' }}>
                {showHtml ? (
                    <TextField
                        multiline
                        fullWidth
                        value={htmlSource}
                        onChange={(e) => { setHtmlSource(e.target.value); setHasChanges(true); }}
                        sx={{
                            height: '100%',
                            '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', fontFamily: 'monospace', fontSize: 13 },
                            '& textarea': { height: '100% !important' },
                        }}
                    />
                ) : (
                    <Box sx={{
                        p: 4,
                        maxWidth: 800,
                        mx: 'auto',
                        '& .ProseMirror': {
                            minHeight: 500,
                            outline: 'none',
                            padding: '24px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                            '&:focus': { borderColor: '#1976d2', boxShadow: '0 0 0 2px rgba(25,118,210,0.15)' },
                            '& p': { margin: '0.5em 0' },
                            '& h1': { fontSize: '2em', fontWeight: 700, margin: '0.5em 0' },
                            '& h2': { fontSize: '1.5em', fontWeight: 700, margin: '0.5em 0' },
                            '& h3': { fontSize: '1.2em', fontWeight: 700, margin: '0.5em 0' },
                            '& table': { borderCollapse: 'collapse', width: '100%', margin: '1em 0' },
                            '& th, & td': { border: '1px solid #ccc', padding: '8px', minWidth: 50 },
                            '& th': { backgroundColor: '#f0f0f0', fontWeight: 600 },
                            '& img': { maxWidth: '100%' },
                            '& hr': { border: 'none', borderTop: '2px solid #999', margin: '1.5em 0' },
                            '& a': { color: '#1976d2', textDecoration: 'underline' },
                            '& blockquote': { borderLeft: '3px solid #ccc', paddingLeft: '16px', color: '#666', margin: '1em 0' },
                            '& ul, & ol': { paddingLeft: '24px' },
                            '& .ProseMirror-placeholder::before': { color: '#999', fontStyle: 'italic' },
                        },
                    }}>
                        <EditorContent editor={editor} />
                    </Box>
                )}
            </Box>

            {/* Save Dialog */}
            <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Vorlage speichern</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Änderungskommentar (optional)"
                        fullWidth
                        value={saveComment}
                        onChange={(e) => setSaveComment(e.target.value)}
                        placeholder="z.B. Logo aktualisiert, Fußzeile angepasst..."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveDialogOpen(false)}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}>
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={handlePreviewClose} maxWidth={isCertificate ? 'lg' : 'md'} fullWidth>
                <DialogTitle>Vorschau{isCertificate ? ' (PDF A4-Querformat)' : ''}</DialogTitle>
                <DialogContent sx={{ p: isCertificate ? 1 : 2 }}>
                    {!previewLoading && (
                        <Alert severity="info" sx={{ mb: 2 }} data-testid="preview-legend">
                            Die Vorschau setzt Testwerte ein. <strong>Rot markierte Platzhalter</strong> kennt
                            diese Vorlagen-Art nicht — meist ein Schreibfehler oder eine Variable aus einer
                            anderen Vorlagen-Art. Beim echten Versand werden sie ersatzlos entfernt, ebenso
                            Variablen, für die im Versandkontext kein Wert existiert (z.B. Fälligkeitsdatum
                            ohne Pflichtzuweisung).
                        </Alert>
                    )}
                    {previewLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : isCertificate ? (
                        <Box sx={{ width: '100%', height: '70vh' }}>
                            {previewPdfUrl && (
                                <iframe
                                    src={previewPdfUrl}
                                    title="Zertifikat-Vorschau"
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                />
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fff' }}>
                            <iframe
                                sandbox=""
                                srcDoc={previewHtml}
                                title="Vorschau"
                                style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePreviewClose}>Schließen</Button>
                </DialogActions>
            </Dialog>

            {/* Version History Drawer */}
            <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)} PaperProps={{ sx: { width: 350 } }}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Versionshistorie</Typography>
                    {versionsLoading ? (
                        <CircularProgress />
                    ) : versions.length === 0 ? (
                        <Typography color="text.secondary">Keine Versionen vorhanden</Typography>
                    ) : (
                        <List>
                            {versions.map(v => (
                                <ListItem key={v.id} disablePadding>
                                    <ListItemButton onClick={() => handleRestore(v)}>
                                        <ListItemText
                                            primary={<>
                                                {`Version ${v.versionNumber}`}
                                                {v.versionNumber === currentVersion && (
                                                    <Chip label="Aktuell" size="small" color="primary" sx={{ ml: 1, height: 20 }} />
                                                )}
                                            </>}
                                            secondary={<>
                                                {new Date(v.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                {v.changeComment && <><br />{v.changeComment}</>}
                                            </>}
                                        />
                                        <Tooltip title="Wiederherstellen"><RestoreIcon fontSize="small" color="action" /></Tooltip>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </Drawer>
        </Box>
    );
};

export default TemplateEditor;
